// MCHD Room Scheduler - Authentication API Endpoints
// API calls for authentication operations

import { TokenManager } from '../interceptors';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:7991/api/v1';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  data?: {
    user: {
      id: string;
      email: string;
      name: string;
      role: 'ADMIN' | 'COORDINATOR' | 'DOCTOR';
    };
    token: string;
    refreshToken: string;
    expiresIn: string;
    refreshExpiresIn: string;
  };
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'COORDINATOR' | 'DOCTOR';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  data?: {
    token: string;
    refreshToken: string;
    expiresIn: string;
    refreshExpiresIn: string;
  };
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

export interface PasswordChangeRequest {
  currentPassword: string;
  newPassword: string;
}

export interface PasswordChangeResponse {
  success: boolean;
  data?: {
    message: string;
  };
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

// =============================================================================
// USER REGISTRATION INTERFACES
// =============================================================================

export interface RegisterRequest {
  email: string;
  name: string;
  password: string;
  role: 'ADMIN' | 'COORDINATOR' | 'DOCTOR';
}

export interface RegisterResponse {
  success: boolean;
  data?: {
    user: User;
  };
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

// =============================================================================
// SESSION MANAGEMENT INTERFACES
// =============================================================================

export interface Session {
  id: string;
  sessionId: string;
  ipAddress: string;
  userAgent: string;
  deviceInfo?: string;
  isActive: boolean;
  lastActivity: string;
  createdAt: string;
}

export interface SessionsResponse {
  success: boolean;
  data?: {
    sessions: Session[];
  };
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

export interface SessionTerminateResponse {
  success: boolean;
  data?: {
    message: string;
  };
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

// =============================================================================
// SECURITY MONITORING INTERFACES
// =============================================================================

export interface FailedAttempt {
  id: string;
  email: string;
  ipAddress: string;
  userAgent?: string;
  attemptedAt: string;
  isResolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  userId?: string;
}

export interface FailedAttemptsResponse {
  success: boolean;
  data?: {
    attempts: FailedAttempt[];
    stats: {
      totalAttempts: number;
      uniqueIPs: number;
      uniqueEmails: number;
      recentAttempts: number;
    };
  };
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

export interface SecurityStatsResponse {
  success: boolean;
  data?: {
    stats: {
      totalAttempts: number;
      unresolvedAttempts: number;
      uniqueEmails: number;
      uniqueIPs: number;
      recentAttempts: number;
      topOffendingIPs: Array<{ ip: string; count: number }>;
      topOffendingEmails: Array<{ email: string; count: number }>;
      totalUsers: number;
      activeUsers: number;
      lockedAccounts: number;
      activeSessions: number;
    };
  };
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

export interface UnlockRequest {
  email?: string;
  ipAddress?: string;
}

export interface UnlockResponse {
  success: boolean;
  data?: {
    message: string;
  };
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

// =============================================================================
// AUDIT LOGGING INTERFACES
// =============================================================================

export interface AuditLog {
  id: string;
  userId?: string;
  action: string;
  entityType?: string;
  entityId?: string;
  details?: any;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface AuditLogFilters {
  userId?: string;
  eventType?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}

export interface AuditLogsResponse {
  success: boolean;
  data?: {
    logs: AuditLog[];
    stats: {
      totalLogs: number;
      uniqueUsers: number;
      eventTypes: string[];
    };
  };
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

export interface AuditReportResponse {
  success: boolean;
  data?: {
    report: {
      summary: {
        totalActions: number;
        uniqueUsers: number;
        dateRange: {
          start: string;
          end: string;
        };
      };
      actions: {
        [key: string]: number;
      };
      users: {
        [key: string]: number;
      };
    };
  };
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

export interface ExportOptions {
  startDate?: string;
  endDate?: string;
  format?: 'json' | 'csv';
}

export interface ExportResponse {
  success: boolean;
  data?: {
    downloadUrl: string;
    filename: string;
    expiresAt: string;
  };
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    timestamp: string;
  };
}

/**
 * Login user with email and password
 * @param credentials - Login credentials
 * @returns Promise<LoginResponse> - Login result
 */
export async function loginUser(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies
    body: JSON.stringify(credentials),
  });

  const data = await response.json();
  return data;
}

/**
 * Logout current user
 * @returns Promise<ApiResponse> - Logout result
 */
export async function logoutUser(): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

/**
 * Get current user information
 * @returns Promise<ApiResponse<User>> - User information
 */
export async function getCurrentUser(): Promise<ApiResponse<User>> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${API_BASE_URL}/auth/me`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

/**
 * Validate JWT token
 * @returns Promise<ApiResponse> - Validation result
 */
// Request deduplication for validateToken
let validateTokenPromise: Promise<ApiResponse> | null = null;

export async function validateToken(): Promise<ApiResponse> {
  // Return existing promise if one is in progress
  if (validateTokenPromise) {
    return validateTokenPromise;
  }

  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  // Create new promise and store it
  validateTokenPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const data = await response.json();
      return data;
    } finally {
      // Clear the promise when done
      validateTokenPromise = null;
    }
  })();

  return validateTokenPromise;
}

/**
 * Refresh JWT token using refresh token
 * @param refreshToken - Refresh token
 * @returns Promise<RefreshTokenResponse> - Token refresh result
 */
export async function refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data;
}

/**
 * Change user password
 * @param passwordData - Password change data
 * @returns Promise<PasswordChangeResponse> - Password change result
 */
export async function changePassword(
  passwordData: PasswordChangeRequest
): Promise<PasswordChangeResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify(passwordData),
  });

  const data = await response.json();
  return data;
}

/**
 * Get authentication service health status
 * @returns Promise<ApiResponse> - Health status
 */
export async function getAuthHealth(): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/health`, {
    method: 'GET',
  });

  const data = await response.json();
  return data;
}

// =============================================================================
// USER REGISTRATION API FUNCTIONS
// =============================================================================

/**
 * Register new user (Admin only)
 * @param userData - User registration data
 * @returns Promise<RegisterResponse> - Registration result
 */
export async function registerUser(
  userData: RegisterRequest
): Promise<RegisterResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify(userData),
  });

  const data = await response.json();
  return data;
}

// =============================================================================
// SESSION MANAGEMENT API FUNCTIONS
// =============================================================================

/**
 * Get all active user sessions
 * @returns Promise<SessionsResponse> - Sessions data
 */
export async function getUserSessions(): Promise<SessionsResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${API_BASE_URL}/auth/sessions`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

/**
 * Terminate specific user session
 * @param sessionId - Session ID to terminate
 * @returns Promise<SessionTerminateResponse> - Termination result
 */
export async function terminateSession(
  sessionId: string
): Promise<SessionTerminateResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${API_BASE_URL}/auth/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

/**
 * Terminate all user sessions except current
 * @returns Promise<SessionTerminateResponse> - Termination result
 */
export async function terminateAllSessions(): Promise<SessionTerminateResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${API_BASE_URL}/auth/sessions`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

/**
 * Update session last activity timestamp
 * @returns Promise<ApiResponse> - Update result
 */
export async function updateSessionActivity(): Promise<ApiResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${API_BASE_URL}/auth/sessions/activity`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

// =============================================================================
// SECURITY MONITORING API FUNCTIONS
// =============================================================================

/**
 * Get failed login attempts (Admin only)
 * @returns Promise<FailedAttemptsResponse> - Failed attempts data
 */
export async function getFailedLoginAttempts(): Promise<FailedAttemptsResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${API_BASE_URL}/auth/failed-attempts`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

/**
 * Unlock user account (Admin only)
 * @param email - Email address to unlock
 * @returns Promise<UnlockResponse> - Unlock result
 */
export async function unlockAccount(email: string): Promise<UnlockResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${API_BASE_URL}/auth/unlock-account`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });

  const data = await response.json();
  return data;
}

/**
 * Unlock IP address (Admin only)
 * @param ipAddress - IP address to unlock
 * @returns Promise<UnlockResponse> - Unlock result
 */
export async function unlockIPAddress(ipAddress: string): Promise<UnlockResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${API_BASE_URL}/auth/unlock-ip`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify({ ipAddress }),
  });

  const data = await response.json();
  return data;
}

/**
 * Get security statistics (Admin only)
 * @returns Promise<SecurityStatsResponse> - Security statistics
 */
export async function getSecurityStats(): Promise<SecurityStatsResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${API_BASE_URL}/auth/security-stats`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

/**
 * Get all users (Admin only)
 * @param params - Query parameters
 * @returns Promise<ApiResponse<{ users: User[], pagination: any }>> - Users data
 */
export async function getAllUsers(
  params: { page?: number; limit?: number; search?: string; role?: string } = {}
): Promise<ApiResponse<{ users: User[]; pagination: any }>> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.role) queryParams.append('role', params.role);

  const response = await fetch(`${API_BASE_URL}/auth/users?${queryParams}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

// =============================================================================
// AUDIT LOGGING API FUNCTIONS
// =============================================================================

/**
 * Get audit logs (Admin only)
 * @param filters - Audit log filters
 * @returns Promise<AuditLogsResponse> - Audit logs data
 */
export async function getAuditLogs(
  filters: AuditLogFilters
): Promise<AuditLogsResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const queryParams = new URLSearchParams();
  
  if (filters.userId) queryParams.append('userId', filters.userId);
  if (filters.eventType) queryParams.append('eventType', filters.eventType);
  if (filters.startDate) queryParams.append('startDate', filters.startDate);
  if (filters.endDate) queryParams.append('endDate', filters.endDate);
  if (filters.limit) queryParams.append('limit', filters.limit.toString());

  const response = await fetch(`${API_BASE_URL}/auth/audit-logs?${queryParams}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

/**
 * Get audit report (Admin only)
 * @param startDate - Report start date
 * @param endDate - Report end date
 * @returns Promise<AuditReportResponse> - Audit report data
 */
export async function getAuditReport(
  startDate: string,
  endDate: string
): Promise<AuditReportResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const queryParams = new URLSearchParams({
    startDate,
    endDate,
  });

  const response = await fetch(`${API_BASE_URL}/auth/audit-report?${queryParams}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

/**
 * Export audit logs (Admin only)
 * @param options - Export options
 * @returns Promise<ExportResponse> - Export result
 */
export async function exportAuditLogs(
  options: ExportOptions
): Promise<ExportResponse> {
  const token = TokenManager.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const queryParams = new URLSearchParams();
  
  if (options.startDate) queryParams.append('startDate', options.startDate);
  if (options.endDate) queryParams.append('endDate', options.endDate);
  if (options.format) queryParams.append('format', options.format);

  const response = await fetch(`${API_BASE_URL}/auth/audit-logs/export?${queryParams}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}
